import {
  Avatar,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  IconBell,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconChartArrowsVertical,
  IconChartDots,
  IconEye,
  IconMessage,
  IconMessages,
  IconPercentage,
  IconSpeakerphone,
  IconTrendingUp,
  IconUsers,
} from "@tabler/icons-react";
import { fetchGet, formatStat, transformFirebaseTime } from "../util/helpers";
import { useContext, useEffect, useMemo, useState } from "react";
import UserContext from "../contexts/UserContext";
import LoadingContext from "../contexts/LoadingContext";
import CreatorCarousel from "../components/CreatorCarousel";
import { useNavigate } from "react-router-dom";

const platformToIcon = {
  youtube: <IconBrandYoutube size="20" color="var(--main-text-color)" />,
  tiktok: <IconBrandTiktok size="20" color="var(--main-text-color)" />,
  facebook: <IconBrandFacebook size="20" color="var(--main-text-color)" />,
  instagram: <IconBrandInstagram size="20" color="var(--main-text-color)" />,
};

function MetricCard({ label, value, icon, iconBg, loading }) {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        borderRadius: "16px",
        background: "#fff",
        border: "1px solid var(--neutral-200)",
        p: "20px",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          borderColor: "var(--secondary-200)",
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography
          variant="body2"
          sx={{ color: "var(--body-text-color-600)", fontWeight: 500, fontSize: "0.8rem" }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "10px",
            backgroundColor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
      {loading ? (
        <Skeleton variant="text" width={80} height={48} sx={{ mt: 0.5 }} />
      ) : (
        <Typography
          variant="h4"
          sx={{ fontWeight: 800, color: "var(--main-text-color)", mt: 0.5 }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
}

function CampaignRow({ campaign, stats, theme, navigate }) {
  return (
    <Box
      sx={{
        borderRadius: "16px",
        background: "#fff",
        border: "1px solid var(--neutral-200)",
        p: "20px 24px",
        transition: "all 0.2s ease",
        cursor: "pointer",
        "&:hover": {
          borderColor: "var(--secondary-200)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          transform: "translateY(-1px)",
        },
      }}
      onClick={() => navigate(`/campaigns/${campaign.id}`)}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(200px, 1.5fr) 90px 90px 90px auto",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Campaign info -- fixed left column */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            src={campaign.image}
            sx={{
              width: 44,
              height: 44,
              border: "2px solid var(--neutral-100)",
              flexShrink: 0,
            }}
          />
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "var(--main-text-color)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {campaign.name}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
              <Chip
                label={campaign.active ? "Active" : "Inactive"}
                icon={
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: campaign.active
                        ? "var(--icon-color-green)"
                        : "var(--icon-color-pink)",
                    }}
                  />
                }
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  backgroundColor: campaign.active
                    ? theme.palette.success.light
                    : theme.palette.error.light,
                  color: campaign.active ? theme.palette.success.main : "white",
                }}
              />
              {campaign.influencers?.length > 0 && (
                <Chip
                  label={campaign.influencers.length}
                  size="small"
                  icon={<IconUsers size="12" color={theme.palette.info.main} />}
                  sx={{
                    height: 24,
                    fontSize: "0.7rem",
                    backgroundColor: theme.palette.info.light,
                    color: theme.palette.info.main,
                  }}
                />
              )}
              {campaign.platforms?.map((p) => (
                <Box key={p} sx={{ display: "flex", alignItems: "center", opacity: 0.7 }}>
                  {platformToIcon[p]}
                </Box>
              ))}
            </Stack>
          </Stack>
        </Stack>

        {/* Views -- fixed column */}
        <Stack alignItems="center" spacing={0.25}>
          <Typography
            variant="caption"
            sx={{
              color: "var(--body-text-color-500)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontSize: "0.6rem",
            }}
          >
            Views
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, color: "var(--main-text-color)" }}
          >
            {stats ? formatStat(stats.views) : "—"}
          </Typography>
        </Stack>

        {/* Engagement -- fixed column */}
        <Stack alignItems="center" spacing={0.25}>
          <Typography
            variant="caption"
            sx={{
              color: "var(--body-text-color-500)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontSize: "0.6rem",
            }}
          >
            Engagement
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, color: "var(--main-text-color)" }}
          >
            {stats ? formatStat(stats.engagement) : "—"}
          </Typography>
        </Stack>

        {/* Eng. Rate -- fixed column */}
        <Stack alignItems="center" spacing={0.25}>
          <Typography
            variant="caption"
            sx={{
              color: "var(--body-text-color-500)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontSize: "0.6rem",
            }}
          >
            Eng. Rate
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, color: "var(--secondary-500)" }}
          >
            {stats ? `${stats.engagementRate}%` : "—"}
          </Typography>
        </Stack>

        {/* Button -- fixed right column */}
        <Button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/campaigns/${campaign.id}`);
          }}
          variant="outlined"
          className="outlined-button"
          color="info"
          sx={{ borderRadius: "10px", fontWeight: 600, whiteSpace: "nowrap", justifySelf: "end" }}
        >
          View Details
        </Button>
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const { user } = useContext(UserContext);
  const { setIsLoading } = useContext(LoadingContext);
  const [creators, setCreators] = useState([]);
  const theme = useTheme();
  const navigate = useNavigate();

  const fetchCreators = () => {
    fetchGet(`/brand/outreach?campaignId=undefined`)
      .then((result) => result.result)
      .then((result) => setCreators(result));
  };

  const fetchCampaigns = async () => {
    const results = await fetchGet("/brand/campaigns", setIsLoading);
    setCampaigns(
      results.result.sort(
        (a, b) =>
          transformFirebaseTime(b.submittedAt) - transformFirebaseTime(a.submittedAt)
      )
    );
  };

  const fetchDashboardStats = async () => {
    setStatsLoading(true);
    try {
      const res = await fetchGet("/brand/dashboard/stats");
      setDashboardStats(res.result);
    } catch {
      setDashboardStats(null);
    }
    setStatsLoading(false);
  };

  useEffect(() => {
    if (user.id) {
      fetchCampaigns();
      fetchCreators();
      fetchDashboardStats();
    }
  }, [user]);

  const campaignStatsMap = useMemo(() => {
    if (!dashboardStats?.campaigns) return {};
    const map = {};
    dashboardStats.campaigns.forEach((s) => {
      map[s.campaignId] = s;
    });
    return map;
  }, [dashboardStats]);

  const totals = dashboardStats?.totals;

  return (
    <Box sx={{ height: "100%", width: "100%", p: "32px 40px", overflow: "hidden", boxSizing: "border-box" }}>
      <Stack direction="column" spacing={4} width="100%">
        {/* Header */}
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: "var(--main-text-color)" }}
          >
            Dashboard
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "var(--body-text-color-500)", mt: 0.5 }}
          >
            Overview of your campaigns and creator performance
          </Typography>
        </Box>

        {/* Metric Cards */}
        <Stack direction="row" spacing={2} width="100%">
          <MetricCard
            label="Active Campaigns"
            value={campaigns?.filter((c) => c.active)?.length || 0}
            icon={
              <IconSpeakerphone size="22" color="var(--icon-color-yellow)" />
            }
            iconBg="var(--background-color-yellow)"
            loading={false}
          />
          <MetricCard
            label="Total Views"
            value={totals ? formatStat(totals.views) : "0"}
            icon={
              <IconEye size="22" color="var(--icon-color-green)" />
            }
            iconBg="var(--background-color-green)"
            loading={statsLoading}
          />
          <MetricCard
            label="Total Engagement"
            value={totals ? formatStat(totals.engagement) : "0"}
            icon={
              <IconMessages size="22" color="var(--icon-color-purple)" />
            }
            iconBg="var(--background-color-purple)"
            loading={statsLoading}
          />
          <MetricCard
            label="Engagement Rate"
            value={totals ? `${totals.engagementRate}%` : "0%"}
            icon={
              <IconPercentage size="22" color="var(--icon-color-pink)" />
            }
            iconBg="var(--background-color-pink)"
            loading={statsLoading}
          />
        </Stack>

        {/* Campaigns Section */}
        <Stack direction="column" spacing={2} width="100%">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: "var(--color-gradient)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconSpeakerphone size="18" color="#fff" />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              My Campaigns
            </Typography>
            <Chip
              label={campaigns?.length || 0}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 700,
                background: "var(--neutral-100)",
                color: "var(--body-text-color-600)",
              }}
            />
          </Stack>

          {campaigns?.length === 0 && !statsLoading && (
            <Box
              sx={{
                borderRadius: "16px",
                border: "1px dashed var(--neutral-200)",
                p: 5,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: "var(--body-text-color-200)" }}
              >
                No campaigns yet. Create your first campaign to get started.
              </Typography>
            </Box>
          )}

          {campaigns
            ?.filter((c) => c)
            ?.map((campaign) => (
              <CampaignRow
                key={campaign.id}
                campaign={campaign}
                stats={campaignStatsMap[campaign.id]}
                theme={theme}
                navigate={navigate}
              />
            ))}
        </Stack>

        {/* Trending Creators */}
        <CreatorCarousel creators={creators} title="Top Trending Creators" />
      </Stack>
    </Box>
  );
}
